import React, {useEffect, useState} from 'react';
import {Menu, X, Zap} from "lucide-react";
import {useAuth0} from "@auth0/auth0-react"
import {Button} from "../Button/Button";
import {NavigationToggle} from "../NavigationToggle/NavigationToggle";
import {useLocation, useNavigate} from "react-router-dom";

export interface NavbarProps extends React.HTMLAttributes<HTMLDivElement> {
}

export const Navbar = ({className, ...props}: NavbarProps) => {

    const {isAuthenticated, logout, user} = useAuth0();
    const location = useLocation();
    const navigate = useNavigate();
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    const isDarkPage =
        location.pathname === "/slideshow" ||
        location.pathname === "/celebrations";

    const handleLogoClick = () => {
        navigate('/');
    };

    const handleLogout = () => {
        setIsMenuOpen(false);
        logout({
            logoutParams: {
                returnTo: window.location.origin,
            },
        });
    };

    const closeMenu = () => setIsMenuOpen(false);

    // Close the drawer whenever the route changes
    useEffect(() => {
        setIsMenuOpen(false);
    }, [location.pathname]);

    // While the drawer is open: lock page scroll and close on Escape
    useEffect(() => {
        if (!isMenuOpen) {
            return;
        }

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setIsMenuOpen(false);
            }
        };
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.body.style.overflow = previousOverflow;
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isMenuOpen]);

    const navBackground = isDarkPage ? 'bg-blue-950 shadow-md shadow-black/20 relative z-50' : 'bg-white shadow-md'

    const textColor = isDarkPage ? 'text-white' : 'text-blue-950';
    const subTextColor = isDarkPage ? 'text-blue-200' : 'text-gray-500';
    const userTextColor = isDarkPage ? 'text-gray-200' : 'text-gray-600';

    const logoutBtnStyles = isDarkPage ? 'bg-white text-blue-950 hover:bg-gray-100 border-transparent' : '';

    const drawerBackground = isDarkPage ? 'bg-blue-950 text-white' : 'bg-white text-blue-950';
    const drawerDivider = isDarkPage ? 'border-white/20' : 'border-gray-200';

    const baseStyles = 'w-full px-3 md:px-6 py-3 md:py-4 transition-all duration-300';
    const combinedClassName = `${baseStyles} ${navBackground} ${className || ''} flex justify-between items-center`;

    return (
        <nav className={combinedClassName} {...props}>
            <div className="flex items-center gap-2 md:gap-4">

                {/* Logo/Icon */}
                <div
                    className="flex items-center justify-center w-9 h-9 bg-gradient-to-br from-blue-500 to-orange-400 rounded-lg shadow-md cursor-pointer"
                    onClick={handleLogoClick}
                >
                    <Zap className="w-5 h-5 text-white"/>
                </div>

                <div className="hidden sm:flex flex-col leading-tight">
                    <span className={`font-bold text-base md:text-lg ${textColor}`}>PowerUp</span>
                    <span className={`text-sm ${subTextColor}`}>
                        Recognition Platform
                    </span>
                </div>
            </div>

            {/* Desktop links (md and up) */}
            <div className="hidden md:flex">
                <NavigationToggle isDarkTheme={isDarkPage}/>
            </div>

            {/* Desktop user block (md and up) */}
            {isAuthenticated ? (
                <div className="hidden md:flex items-center gap-4">

                    {user?.picture && (
                        <img
                            src={user.picture}
                            alt={user.name}
                            className="w-9 h-9 rounded-full object-cover border border-gray-300 dark:border-white/30 shadow-sm transition-transform duration-200 hover:scale-105"
                        />
                    )}

                    <span className={userTextColor}>{user?.name}</span>

                    <Button
                        variant="default"
                        onClick={handleLogout}
                        className={`px-4 py-2 text-sm ${logoutBtnStyles}`}
                    >
                        Logout
                    </Button>

                </div>
            ) : (
                <div className="hidden md:block"/>
            )}

            {/* Hamburger button (mobile only) */}
            <button
                type="button"
                className={`md:hidden p-2 rounded-lg ${textColor} ${isDarkPage ? 'hover:bg-white/10' : 'hover:bg-gray-100'} focus:outline-none focus:ring-2 focus:ring-blue-500`}
                onClick={() => setIsMenuOpen(open => !open)}
                aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={isMenuOpen}
                aria-controls="mobile-menu"
            >
                {isMenuOpen ? <X className="w-6 h-6"/> : <Menu className="w-6 h-6"/>}
            </button>

            {/* Mobile drawer */}
            {isMenuOpen && (
                <div className="md:hidden fixed inset-0 z-50">
                    {/* Backdrop */}
                    <div
                        className="absolute inset-0 bg-black/50"
                        onClick={closeMenu}
                        aria-hidden="true"
                    />

                    {/* Panel */}
                    <aside
                        id="mobile-menu"
                        className={`absolute inset-y-0 right-0 w-64 max-w-[80%] flex flex-col p-4 shadow-xl ${drawerBackground}`}
                    >
                        <div className="flex justify-end mb-2">
                            <button
                                type="button"
                                className={`p-2 rounded-lg ${isDarkPage ? 'hover:bg-white/10' : 'hover:bg-gray-100'} focus:outline-none focus:ring-2 focus:ring-blue-500`}
                                onClick={closeMenu}
                                aria-label="Close menu"
                            >
                                <X className="w-6 h-6"/>
                            </button>
                        </div>

                        {isAuthenticated && (
                            <div className={`flex items-center gap-3 pb-4 mb-4 border-b ${drawerDivider}`}>
                                {user?.picture && (
                                    <img
                                        src={user.picture}
                                        alt={user.name}
                                        className="w-10 h-10 rounded-full object-cover border border-gray-300 shadow-sm"
                                    />
                                )}
                                <span className={`font-medium truncate ${userTextColor}`}>{user?.name}</span>
                            </div>
                        )}

                        <NavigationToggle
                            isDarkTheme={isDarkPage}
                            orientation="vertical"
                            onNavigate={closeMenu}
                        />

                        {isAuthenticated && (
                            <div className={`mt-auto pt-4 border-t ${drawerDivider}`}>
                                <Button
                                    variant="default"
                                    onClick={handleLogout}
                                    className={`w-full py-2 text-sm ${logoutBtnStyles}`}
                                >
                                    Logout
                                </Button>
                            </div>
                        )}
                    </aside>
                </div>
            )}
        </nav>
    );
};
